import { useEffect, useState } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ImageUpload } from "@/components/image-upload";
import { Plus, Pencil, Trash2, FileText, Calendar, Users, GraduationCap, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useUserRole } from "@/hooks/use-user-role";


export const Route = createFileRoute("/_authenticated/announcements")({
  head: () => ({ meta: [{ title: "Pengumuman — MI Al-Munajah Admin" }] }),
  component: AnnouncementsPage,
});

type Category = "sekolah" | "akademik" | "kegiatan";
type Ann = {
  id: string; title: string; content: string; category: Category;
  date: string; image_url: string | null; pdf_url: string | null;
};
const empty = {
  title: "", content: "", category: "sekolah" as Category,
  date: new Date().toISOString().slice(0, 10),
  image_url: null as string | null, pdf_url: null as string | null,
};

const catColor: Record<Category, string> = {
  sekolah: "bg-primary/15 text-primary",
  akademik: "bg-info/15 text-info",
  kegiatan: "bg-warning/15 text-warning",
};

function AnnouncementsPage() {
  const { isAdmin, role } = useUserRole();
  const isSuperadmin = role === "superadmin";
  const navigate = useNavigate();
  const [rows, setRows] = useCachedState<Ann[]>("announcements:rows", []);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Ann | null>(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data, error } = await supabase.from("announcements").select("*").order("date", { ascending: false }).order("created_at", { ascending: false });
    if (!error && data) setRows(data as Ann[]);
  };
  useEffect(() => {
    load();
    const ch = supabase.channel("ann-rt").on("postgres_changes", { event: "*", schema: "public", table: "announcements" }, load).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const startNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (a: Ann) => {
    setEditing(a);
    setForm({ title: a.title, content: a.content, category: a.category, date: a.date, image_url: a.image_url, pdf_url: a.pdf_url });
    setOpen(true);
  };
  const save = async () => {
    if (!form.title || !form.content) return toast.error("Judul dan isi wajib diisi");
    const { error } = editing
      ? await supabase.from("announcements").update(form).eq("id", editing.id)
      : await supabase.from("announcements").insert(form);
    if (error) return toast.error(error.message);
    toast.success(editing ? "Pengumuman diperbarui" : "Pengumuman dipublikasi & dikirim ke siswa");
    setOpen(false);
  };
  const remove = async (id: string) => {
    const { error } = await supabase.from("announcements").delete().eq("id", id);
    if (error) toast.error(error.message); else toast.success("Pengumuman dihapus");
  };

  return (
    <div>
      <PageHeader
        title="Pengumuman"
        description="Publikasikan informasi ke aplikasi siswa secara realtime."
        actions={
          isAdmin ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="rounded-xl">
                  <Plus className="w-4 h-4 mr-2" /> Buat Pengumuman <ChevronDown className="w-4 h-4 ml-1 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuItem onClick={startNew} className="cursor-pointer py-2.5">
                  <Users className="w-4 h-4 mr-2 text-primary" />
                  <div>
                    <div className="font-medium text-sm">Untuk Siswa</div>
                    <div className="text-xs text-muted-foreground">Tampil di aplikasi siswa</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate({ to: "/staff-announcements", search: { new: 1 } as never })}
                  className="cursor-pointer py-2.5"
                >
                  <GraduationCap className="w-4 h-4 mr-2 text-info" />
                  <div>
                    <div className="font-medium text-sm">Untuk Staf Pengajar</div>
                    <div className="text-xs text-muted-foreground">Khusus guru &amp; bendahara</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button onClick={startNew} className="rounded-xl">
              <Plus className="w-4 h-4 mr-2" /> Tambah Pengumuman
            </Button>
          )
        }
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit Pengumuman" : "Tambah Pengumuman Siswa"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Judul *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v as Category })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sekolah">Sekolah</SelectItem>
                    <SelectItem value="akademik">Akademik</SelectItem>
                    <SelectItem value="kegiatan">Kegiatan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Isi Pengumuman *</Label><Textarea rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Lampiran Foto</Label>
              <ImageUpload bucket="announcements" value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} />
            </div>
            <div className="space-y-1.5"><Label>Lampiran PDF</Label>
              <ImageUpload bucket="announcements" accept="application/pdf" value={form.pdf_url} onChange={(url) => setForm({ ...form, pdf_url: url })} label="Upload PDF" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} className="rounded-xl">{editing ? "Simpan" : "Publikasi"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.length === 0 && <div className="col-span-full text-center text-muted-foreground py-16">Belum ada pengumuman</div>}
        {rows.map((a) => (
          <Card key={a.id} className="p-5 rounded-2xl flex flex-col hover:shadow-[var(--shadow-elevation-2)] transition-shadow">
            {a.image_url && <img src={a.image_url} alt="" className="w-full h-36 object-cover rounded-xl mb-3 -mt-1" />}
            <div className="flex items-center gap-2 mb-2">
              <Badge className={`rounded-full ${catColor[a.category]} border-0`}>{a.category}</Badge>
              <span className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(a.date).toLocaleDateString("id-ID")}</span>
            </div>
            <h3 className="font-semibold mb-1">{a.title}</h3>
            <p className="text-sm text-muted-foreground line-clamp-3 flex-1">{a.content}</p>
            {a.pdf_url && <a href={a.pdf_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary mt-3 inline-flex items-center gap-1"><FileText className="w-3 h-3" /> Buka PDF</a>}
            <div className="mt-4 pt-3 border-t flex justify-end gap-1">
              <Button variant="ghost" size="icon" onClick={() => startEdit(a)}><Pencil className="w-4 h-4" /></Button>
              {isSuperadmin && (
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="ghost" size="icon"><Trash2 className="w-4 h-4 text-destructive" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Hapus pengumuman?</AlertDialogTitle><AlertDialogDescription>Tindakan tidak dapat dibatalkan.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => remove(a.id)}>Hapus</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
